import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  AdminTenant,
  AdminTenantCreatePayload,
  AdminTenantSummary,
  AdminTenantUpdatePayload,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class AdminTenantsService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/admin/tenants';

  list(): Observable<AdminTenantSummary[]> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(this.basePath, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  get(id: string): Observable<AdminTenant> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/${id}`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw, true as const)));
  }

  create(payload: AdminTenantCreatePayload): Observable<AdminTenantSummary> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .post<unknown>(this.basePath, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw)));
  }

  update(id: string, payload: AdminTenantUpdatePayload): Observable<AdminTenant> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .put<unknown>(`${this.basePath}/${id}`, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw, true as const)));
  }

  delete(id: string): Observable<void> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete<void>(`${this.basePath}/${id}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  private normalizeList(raw: unknown): AdminTenantSummary[] {
    if (Array.isArray(raw)) return raw.map((t) => this.normalizeOne(t));
    if (raw && typeof raw === 'object') {
      const items = (raw as Record<string, unknown>)['items'] ?? (raw as Record<string, unknown>)['data'];
      if (Array.isArray(items)) return items.map((t) => this.normalizeOne(t));
    }
    return [];
  }

  private normalizeOne(raw: unknown): AdminTenantSummary;
  private normalizeOne(raw: unknown, asFull: true): AdminTenant;
  private normalizeOne(raw: unknown, asFull?: boolean): AdminTenantSummary | AdminTenant {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const is_active = o['is_active'] !== false;
    const base: AdminTenantSummary = {
      id: String(o['id'] ?? ''),
      name: String(o['name'] ?? ''),
      slug: String(o['slug'] ?? ''),
      logo_url: o['logo_url'] != null ? String(o['logo_url']) : null,
      primary_color: o['primary_color'] != null ? String(o['primary_color']) : undefined,
      secondary_color: o['secondary_color'] != null ? String(o['secondary_color']) : undefined,
      description: o['description'] != null ? String(o['description']) : undefined,
      is_active,
      created_at: o['created_at'] != null ? String(o['created_at']) : undefined,
      updated_at: o['updated_at'] != null ? String(o['updated_at']) : undefined,
    };
    if (asFull) {
      return { ...base, is_active };
    }
    return base;
  }
}
